
import { useState, useEffect } from 'react';
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Project, ProjectTask } from "../../types";

interface ProjectCostAnalysis {
  id: string;
  project_id: string;
  user_id: string;
  base_cost: number;
  total_cost: number;
  total_with_buffer: number;
  suggested_buffer: number;
  director_percent: number;
  director_amount: number;
  manager_percent: number;
  manager_amount: number;
  teamlead_percent: number;
  teamlead_amount: number;
  teammember_percent: number;
  teammember_amount: number;
  risk_considerations: string[];
  created_at: string;
  updated_at: string;
}

interface ResourceAssignment {
  id: string;
  task_id: string;
  name: string;
  role: string;
  rate: number;
  allocation_hrs: string;
  duration: number;
  allocated_percentage: number;
  start_date: string;
  end_date: string;
}

interface TaskAssignmentCache {
  [taskId: string]: {
    assignments: ResourceAssignment[];
    lastFetched: number;
  };
}

export const useCostAnalysisOptimized = (selectedProject: Project | null) => {
  const [costAnalysis, setCostAnalysis] = useState<ProjectCostAnalysis | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [allResourceAssignments, setAllResourceAssignments] = useState<ResourceAssignment[]>([]);
  const [projectTasks, setProjectTasks] = useState<ProjectTask[]>([]);
  const [assignmentCache, setAssignmentCache] = useState<TaskAssignmentCache>({});
  const [isAllDataFetched, setIsAllDataFetched] = useState(false);

  // Cache duration: 5 minutes
  const CACHE_DURATION = 5 * 60 * 1000;

  useEffect(() => {
    if (selectedProject) {
      loadProjectTasks();
      fetchCostAnalysis();
    }
  }, [selectedProject]);

  useEffect(() => {
    if (projectTasks.length > 0) {
      fetchAllResourceAssignments();
    }
  }, [projectTasks]);

  const loadProjectTasks = () => {
    if (!selectedProject?.tasks) {
      setProjectTasks([]);
      return;
    }
    
    console.log('📋 COST ANALYSIS OPTIMIZATION: Loading project tasks from project.tasks JSON:', {
      projectId: selectedProject.id,
      totalTasks: selectedProject.tasks.length,
      sampleTasks: selectedProject.tasks.slice(0, 3).map(t => ({
        taskId: t.taskId || t.name,
        name: t.name,
        duration: t.duration
      }))
    });

    // Convert project tasks and generate unique task IDs
    const tasksWithIds = selectedProject.tasks.map((task, index) => ({
      ...task,
      taskId: task.taskId || task.name || `Task-${index + 1}`,
      id: task.id || `task-${index + 1}`
    }));

    setProjectTasks(tasksWithIds);
  };

  const fetchCostAnalysis = async () => {
    if (!selectedProject) return;
    setIsLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('project_cost_analysis')
        .select('*')
        .eq('project_id', selectedProject.id)
        .eq('user_id', user.id)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching cost analysis:', error);
        toast.error('Failed to load cost analysis');
        return;
      }

      if (data) {
        const typedData: ProjectCostAnalysis = {
          ...data,
          risk_considerations: Array.isArray(data.risk_considerations) ? data.risk_considerations as string[] : [],
          teamlead_amount: data.teamlead_amount || (data.teamlead_percent ? (data.teamlead_percent / 100) * data.total_cost : 0)
        };
        setCostAnalysis(typedData);
      }
    } catch (error) {
      console.error('Error in fetchCostAnalysis:', error);
      toast.error('Failed to load cost analysis');
    }
  };

  const fetchTaskAssignments = async (taskId: string) => {
    if (!selectedProject || !taskId) return [];
    
    // Check cache first
    const cached = assignmentCache[taskId];
    const now = Date.now();
    
    if (cached && (now - cached.lastFetched) < CACHE_DURATION) {
      console.log('📈 COST OPTIMIZATION: Using cached assignments for task:', taskId);
      return cached.assignments;
    }
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];

      console.log('🔄 COST OPTIMIZATION: Fetching assignments for task:', {
        taskId,
        projectId: selectedProject.id
      });

      const { data, error } = await supabase
        .from('resource_assignment')
        .select('*')
        .eq('project_id', selectedProject.id)
        .eq('task_id', taskId)
        .eq('user_id', user.id);

      if (error) throw error;

      const assignments = data || [];
      
      console.log('✅ COST SUCCESS: Task assignments fetched:', {
        taskId,
        assignmentCount: assignments.length
      });

      // Update cache
      setAssignmentCache(prev => ({
        ...prev,
        [taskId]: {
          assignments,
          lastFetched: now
        }
      }));

      return assignments;
    } catch (error) {
      console.error('Error fetching task assignments:', error);
      return [];
    }
  };

  const fetchAllResourceAssignments = async () => {
    if (!selectedProject || projectTasks.length === 0) return;

    console.log('🚀 COST OPTIMIZATION: Starting optimized fetch for all tasks:', {
      projectId: selectedProject.id,
      totalTasks: projectTasks.length
    });

    setIsLoading(true);
    setIsAllDataFetched(false);
    
    try {
      const allAssignments: ResourceAssignment[] = [];
      
      // Fetch assignments for each task individually
      for (const task of projectTasks) {
        const taskId = task.taskId || task.name;
        if (taskId) {
          const taskAssignments = await fetchTaskAssignments(taskId);
          allAssignments.push(...taskAssignments);
        }
      }

      console.log('✅ COST OPTIMIZATION COMPLETE: All assignments fetched and ready for calculations:', {
        totalAssignments: allAssignments.length,
        tasksProcessed: projectTasks.length,
        allDataFetched: true
      });

      // Set all assignments and mark as complete
      setAllResourceAssignments(allAssignments);
      setIsAllDataFetched(true);
    } catch (error) {
      console.error('Error in fetchAllResourceAssignments:', error);
      toast.error('Failed to load resource assignments for cost analysis');
    } finally {
      setIsLoading(false);
    }
  };

  const refreshResourceAssignments = () => {
    // Clear cache and reset completion state to force fresh data
    setAssignmentCache({});
    setIsAllDataFetched(false);
    if (projectTasks.length > 0) {
      fetchAllResourceAssignments();
    }
  };

  return {
    costAnalysis,
    isLoading,
    resourceAssignments: allResourceAssignments,
    isAllDataFetched,
    fetchCostAnalysis,
    fetchResourceAssignments: refreshResourceAssignments
  };
};
